import "./InviteCodeCard.css";
import { createSignal, onMount, Show } from "solid-js";
import type { JSX } from "solid-js";
import { Button, ButtonMonochrome } from "@ui/Button";
import { Card } from "@ui/Card";
import { InputContainer } from "@ui/InputContainer";
import { Loader } from "@ui/Loader";
import { HStack, VStack } from "@ui/Stack";
import { Text } from "@ui/typography/Text";
import { getCookie } from "~/modules/shared/utils/auth";
import { defaultButtonLeads } from "~/modules/shared/utils/platform";
import { invitationLinkCode } from "./invitationLinkCode";

/** Room for a whole pasted invitation link, which the input turns into its code. */
const MAX_INPUT_LENGTH = 2048;

type Props = {
	inputStyle?: JSX.CSSProperties;
	onAccepted: (code: string) => void;
	onCancel: () => void;
};

/**
 * Takes an invite code typed in on /sign-in, with the same effect as arriving
 * via the invitation link: the server keeps the code in the session.
 */
export const InviteCodeCard = (props: Props) => {
	const [code, setCode] = createSignal("");
	const [submitting, setSubmitting] = createSignal(false);
	const [errorMessage, setErrorMessage] = createSignal("");
	let inputRef: HTMLInputElement | undefined;

	onMount(() => inputRef?.focus());

	const submit = async () => {
		const entered = code().trim();
		if (entered === "" || submitting()) return;
		setSubmitting(true);
		setErrorMessage("");

		try {
			const response = await fetch("/creator-signup/code", {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					"Accept": "application/json",
					"X-XSRF-TOKEN": getCookie("XSRF-TOKEN") ?? "",
				},
				body: JSON.stringify({ code: entered }),
			});

			if (response.ok) {
				const data = await response.json();
				props.onAccepted(data.code);
				return;
			}
			setErrorMessage(
				response.status === 429
					? "Too many attempts. Please wait a few minutes and try again."
					: "That invite code isn't valid. Check it and try again.",
			);
		} catch {
			setErrorMessage("Unable to connect. Please check your connection and try again.");
		} finally {
			setSubmitting(false);
		}
	};

	const cancel = (
		<ButtonMonochrome disabled={submitting()} onClick={() => props.onCancel()}>
			Cancel
		</ButtonMonochrome>
	);
	const useCode = (
		<Button
			class="invite-code-card__use"
			disabled={code().trim() === "" || submitting()}
			onClick={() => void submit()}
		>
			<span
				class="invite-code-card__use-label"
				classList={{ "invite-code-card__use-label--busy": submitting() }}
			>
				Use code
			</span>
			<Show when={submitting()}>
				<span class="invite-code-card__use-loader">
					<Loader size="xsmall" color="var(--white)" />
				</span>
			</Show>
		</Button>
	);

	return (
		<div
			class="invite-code-card"
			role="dialog"
			aria-label="Enter your invite code"
			onKeyDown={(e) => {
				if (e.key === "Escape" && !submitting()) props.onCancel();
			}}
		>
			<Card>
				<VStack spacing="medium" fullWidth>
					<Text center>Please enter your invite code</Text>

					<VStack spacing="small" fullWidth>
						<InputContainer>
							<input
								ref={inputRef}
								type="text"
								aria-label="Invite code"
								maxlength={MAX_INPUT_LENGTH}
								autocomplete="off"
								autocapitalize="characters"
								spellcheck={false}
								style={props.inputStyle}
								value={code()}
								disabled={submitting()}
								onInput={(e) => {
									const value = e.currentTarget.value;
									const linkCode = invitationLinkCode(value);
									// Set on the field too: pasting the link of the code
									// already entered leaves the signal unchanged, so the
									// binding alone wouldn't replace the link.
									if (linkCode !== null) e.currentTarget.value = linkCode;
									setCode(linkCode ?? value);
									setErrorMessage("");
								}}
								onKeyDown={(e) => {
									if (e.key === "Enter") void submit();
								}}
							/>
						</InputContainer>

						<div aria-live="polite">
							<Text color="error" size="small" class="invite-code-card__error">
								{errorMessage()}
							</Text>
						</div>
					</VStack>

					<HStack spacing="large" justify="center" fullWidth>
						<Show
							when={defaultButtonLeads()}
							fallback={
								<>
									{cancel}
									{useCode}
								</>
							}
						>
							{useCode}
							{cancel}
						</Show>
					</HStack>
				</VStack>
			</Card>
		</div>
	);
};
